/**
 * This file started off from the @react-aria's `useSelect` implementation
 * which can be found here
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/select/src/useSelect.ts
 *
 * At the time of writing, @react-aria did not directly support select or
 * combobox with multiple selection. The reason being that there is no such
 * ARIA pattern and some interactions of a multiselect can't be translated to a
 * native <select> which is what `useSelect` tries to emulate.
 *
 * Some relevant discussions can be found here:
 * https://github.com/adobe/react-spectrum/issues/2140
 * https://github.com/adobe/react-spectrum/discussions/2218
 */

import { AriaButtonProps } from 'react-aria/useButton';
import { AriaListBoxOptions } from 'react-aria/useListBox';
import { AriaSelectProps } from '@react-types/select';
import { chain } from 'react-aria/chain';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useId } from 'react-aria/useId';
import {
  DOMAttributes,
  FocusableElement,
  MultipleSelection,
} from '@react-types/shared';
import { FocusEvent, RefObject, useMemo } from 'react';
import { ListKeyboardDelegate } from 'react-aria/ListKeyboardDelegate';
import { useTypeSelect } from 'react-aria/private/selection/useTypeSelect';
import { setInteractionModality } from 'react-aria/private/interactions/useFocusVisible';
import { useCollator } from '@react-aria/i18n';
import { useField } from 'react-aria/useField';
import { useMenuTrigger } from 'react-aria/useMenu';
import { MultiSelectState } from './useMultiSelectState';
import {
  BaseComponentKeys,
  FocusEventsKeys,
  KeyboardEventsKeys,
} from '../../utils/types';

// Omit all single-selection related props from SelectProps and add all
// multi-selection related props.
type MultiSelectOptions<T> = Pick<
  AriaSelectProps<T>,
  BaseComponentKeys | 'isDisabled' | KeyboardEventsKeys | FocusEventsKeys
> &
  MultipleSelection;

export interface MultiSelectAria<T> {
  /** Props for the label element. */
  labelProps: DOMAttributes;

  /** Props for the popup trigger element. */
  triggerProps: AriaButtonProps;

  /** Props for the element representing the selected value. */
  valueProps: DOMAttributes;

  /** Props for the popup. */
  menuProps: AriaListBoxOptions<T>;

  /** Props for the select's description element, if any. */
  descriptionProps: DOMAttributes;

  /** Props for the select's error message element, if any. */
  errorMessageProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a multiselect
 * component. A multiselect displays a collapsible list of options and allows a user
 * to select one or more of them.
 * @param props - Props for the multiselect.
 * @param state - State for the multiselect, as returned by `useMultiSelectState`.
 *
 */
export function useMultiSelect<T>(
  props: MultiSelectOptions<T>,
  state: MultiSelectState<T>,
  ref: RefObject<FocusableElement>
): MultiSelectAria<T> {
  const { isDisabled } = props;

  // By default, a KeyboardDelegate is provided which uses the DOM to query
  // layout information (e.g. for page up/page down). When virtualized, the
  // layout object will be passed in as a prop and override this.
  const collator = useCollator({ usage: 'search', sensitivity: 'base' });
  const delegate = useMemo(
    () =>
      new ListKeyboardDelegate(
        state.collection,
        state.disabledKeys,
        null as never,
        collator
      ),
    [state.collection, state.disabledKeys, collator]
  );

  const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
    {
      isDisabled,
      type: 'listbox',
    },
    state,
    ref
  );

  const { typeSelectProps } = useTypeSelect({
    keyboardDelegate: delegate,
    selectionManager: state.selectionManager,
    onTypeSelect(key) {
      if (!state.selectionManager.isSelectAll) {
        state.selectionManager.select(key);
      }
    },
  });

  const { labelProps, fieldProps, descriptionProps, errorMessageProps } =
    useField({
      ...props,
      labelElementType: 'span',
    });

  typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;
  delete typeSelectProps.onKeyDownCapture;

  const domProps = filterDOMProps(props, { global: true, labelable: true });
  const triggerProps = mergeProps(
    typeSelectProps,
    menuTriggerProps,
    fieldProps
  );

  const valueId = useId();

  return {
    labelProps: {
      ...labelProps,
      onClick: () => {
        if (!props.isDisabled) {
          ref.current?.focus();

          // Show the focus ring so the user knows where focus went
          setInteractionModality('keyboard');
        }
      },
    },
    triggerProps: mergeProps(domProps, {
      ...triggerProps,
      isDisabled,
      onKeyDown: chain(triggerProps.onKeyDown, props.onKeyDown),
      onKeyUp: props.onKeyUp,
      'aria-labelledby': [
        triggerProps['aria-labelledby'],
        triggerProps['aria-label'] && !triggerProps['aria-labelledby']
          ? triggerProps.id
          : null,
        valueId,
      ]
        .filter(Boolean)
        .join(' '),
      onFocus(e: FocusEvent) {
        if (state.isFocused) {
          return;
        }

        if (props.onFocus) {
          props.onFocus(e);
        }

        if (props.onFocusChange) {
          props.onFocusChange(true);
        }

        state.setFocused(true);
      },
      onBlur(e: FocusEvent) {
        if (state.isOpen) {
          return;
        }

        if (props.onBlur) {
          props.onBlur(e);
        }

        if (props.onFocusChange) {
          props.onFocusChange(false);
        }

        state.setFocused(false);
      },
    }),
    valueProps: {
      id: valueId,
    },
    menuProps: {
      ...menuProps,
      autoFocus: state.focusStrategy || true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
      disallowEmptySelection: true,
      onBlur: (e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }

        if (props.onBlur) {
          props.onBlur(e);
        }

        if (props.onFocusChange) {
          props.onFocusChange(false);
        }

        state.setFocused(false);
      },
      'aria-labelledby': [
        fieldProps['aria-labelledby'],
        triggerProps['aria-label'] && !fieldProps['aria-labelledby']
          ? triggerProps.id
          : null,
      ]
        .filter(Boolean)
        .join(' '),
    },
    descriptionProps,
    errorMessageProps,
  };
}
